import { Platform } from 'react-native';
import { httpsCallable as webHttpsCallable } from 'firebase/functions';
import { functions as webFunctions } from './firebaseConfig';

// =============================================================================
// Wrappers tipados de las Cloud Functions callable.
// Punto único para invocar funciones del backend sin tener que importar
// httpsCallable + region en cada pantalla.
//
// ⚠️ BLOQUEADOR CRÍTICO ARREGLADO (auditoría de producción):
// En el APK Android la sesión REAL vive en el SDK NATIVO
// (@react-native-firebase/auth, vía custom-token). El SDK WEB de Firebase
// (firebase/functions) NUNCA se firma en nativo (su `auth` es un Proxy que
// estalla — ver firebaseConfig.ts). Por eso, cuando estos callables se creaban
// con `httpsCallable(functions_web, ...)`, en el APK salían SIN request.auth y
// TODAS las funciones que exigen auth (createFoodOrder, submitCandidateOnboarding,
// requestPayoutSecure, getDriverPublicInfo, claim/pickup/deliverFoodOrder,
// submitRideRating, cupones, etc.) fallaban con 'unauthenticated'. Los VIAJES
// sobrevivían porque usan Firestore nativo + trigger, no callables — por eso el
// bug no aparecía probando solo viajes, ni en web (ahí el auth web sí está logueado).
//
// FIX: en nativo enrutamos por @react-native-firebase/functions (modular v24),
// que hereda la sesión nativa y adjunta el ID token. En web seguimos con el SDK
// web. Ambos exponen la misma forma `(data) => Promise<{ data }>`, así que NINGÚN
// call site cambia. La región por defecto (us-central1) coincide en ambos SDKs.
// =============================================================================

type Callable<I, O> = (data?: I) => Promise<{ data: O }>;

// Fábrica de callables nativos — se resuelve una sola vez (lazy require para no
// romper el bundle web, donde el módulo nativo no existe).
let _nativeFactory: ((name: string) => (data?: any) => Promise<{ data: any }>) | null = null;
function nativeCallableFactory() {
  if (_nativeFactory) return _nativeFactory;
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { getApp } = require('@react-native-firebase/app');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { getFunctions, httpsCallable } = require('@react-native-firebase/functions');
  const fns = getFunctions(getApp());
  _nativeFactory = (name: string) => {
    const fn = httpsCallable(fns, name);
    return (data?: any) => fn(data);
  };
  return _nativeFactory;
}

function makeCallable<I, O>(name: string): Callable<I, O> {
  if (Platform.OS !== 'web') {
    let cached: ((data?: any) => Promise<{ data: any }>) | null = null;
    return (data?: I) => {
      if (!cached) cached = nativeCallableFactory()(name);
      return cached(data) as Promise<{ data: O }>;
    };
  }
  const webFn = webHttpsCallable<I, O>(webFunctions, name);
  return (data?: I) => webFn(data as any);
}

export interface CreateFoodOrderInput {
  restaurantId: string;
  restaurantOwnerId?: string;
  restaurantName?: string;
  customerName?: string;
  customerPhone?: string;
  items: string;
  /** Subtotal de comida BRUTO (sin delivery/propina/cupón) — el servidor lo
   *  valida contra los precios reales del menú (anti-manipulación). */
  subtotal?: number;
  /** Total BRUTO = subtotal + delivery + propina, SIN restar el cupón. El
   *  servidor aplica el cupón una sola vez (evita el doble descuento). */
  total: number;
  deliveryAddress?: string;
  paymentMethod: 'CASH' | 'TRANSFER' | 'CARD';
  receiptImage?: string | null;
}

export interface CreateFoodOrderResult {
  orderId: string;
  securityPin: number;
  /** Solo presente cuando paymentMethod=CARD — pasar a Stripe Payment Sheet */
  clientSecret?: string | null;
}

export const callCreateFoodOrder = makeCallable<CreateFoodOrderInput, CreateFoodOrderResult>(
  'createFoodOrder'
);

// ── Driver info pública para pasajeros ──────────────────────────────────────
// ESTA INTERFAZ ES EL CONTRATO CON `getDriverPublicInfo` (functions/src/drivers.ts).
// Tiene que decir exactamente lo que el servidor devuelve, ni un campo más:
// una interfaz que promete un campo que no llega hace que la pantalla lo pinte
// vacío sin que nadie vea un error.
//
// Qué cambió y por qué:
//  · `vehicleInfo` FUERA. El servidor dejó de devolverlo (nadie lo escribía en
//    /users, salía null en el 100% de las llamadas). Ahora vienen los cuatro
//    campos sueltos del carro, que sí existen y los escribe el KYC.
//  · `rating` puede ser NULL. Sin calificaciones no hay nota: el '5.0' de
//    relleno le ponía cinco estrellas a un chofer que nadie ha calificado.
//    `ratingCount` dice cuántas notas hay detrás (null = sin dato).
//  · `isDriverVerified` es el sello REAL de kyc.ts, para que el escudo verde
//    pueda decir que no.
export interface DriverPublicInfo {
  uid: string;
  userName: string | null;
  photoURL: string | null;
  /** null = todavía no lo ha calificado nadie. Pintar "Nuevo", nunca "5.0". */
  rating: number | null;
  /** Cuántas calificaciones hay detrás de `rating`. null = sin dato. */
  ratingCount: number | null;
  isDriverVerified: boolean;
  vehicleType: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  vehicleColor: string | null;
  licensePlate: string | null;
  /** Solo llega si quien llama es el pasajero de ese viaje activo. */
  bankAccounts?: any[];
}

export const callGetDriverPublicInfo = makeCallable<
  { driverId: string; rideId?: string },
  DriverPublicInfo
>('getDriverPublicInfo');

// ── KYC MANUAL ──────────────────────────────────────────────────────────────
// Reemplaza la comparación facial de AWS Rekognition que vivía en Railway
// (borrado, 404). Ahora el usuario sube cédula y selfie a Firebase Storage y
// envía la solicitud; un humano la revisa desde el panel de admin.
//
// `kycCompleted` SOLO lo escribe `reviewKycSubmission` con Admin SDK: las
// reglas de Firestore bloquean ese campo al cliente, así nadie se
// auto-verifica. Y `submitKycForReview` valida que las URLs de las fotos
// pertenezcan a la carpeta de Storage del propio usuario, para que nadie
// pueda enviar la cédula de otra persona.
export interface SubmitKycInput {
  /** Mapa tipo-de-documento → URL de descarga en kyc_documents/{uid}/… */
  documents: Record<string, string>;
  /** URL de la selfie (user_avatars/{uid}/… o kyc_documents/{uid}/…) */
  selfieUrl?: string | null;
}

export const callSubmitKycForReview = makeCallable<
  SubmitKycInput,
  { ok: boolean; status: 'PENDING' }
>('submitKycForReview');

// Panel de admin: cola de verificaciones y decisión. Exigen claim admin + 2FA.
export const callListPendingKycSubmissions = makeCallable<
  { limit?: number; status?: 'PENDING' | 'APPROVED' | 'REJECTED' },
  { ok: boolean; total: number; solicitudes: any[] }
>('listPendingKycSubmissions');

export const callReviewKycSubmission = makeCallable<
  { uid: string; decision: 'APPROVED' | 'REJECTED'; reason?: string },
  { ok: boolean; decision: string; uid: string }
>('reviewKycSubmission');

// ── La multa de cancelaciones ───────────────────────────────────────────────
// Cobra la multa del saldo que la persona YA tiene en la app. NO desbloquea la
// cuenta: eso lo hace el admin desde el panel tras revisar el caso.
export const callPagarMultaConSaldo = makeCallable<
  Record<string, never>,
  { ok: boolean; yaEstaba: boolean; pagado: number; saldoDespues: number }
>('pagarMultaConSaldo');

// ── Los vehículos del chofer ────────────────────────────────────────────────
// Varios vehículos por chofer, UNO activo (como Uber). La lista vive en
// users/{uid}/vehiculos y la escribe SOLO el servidor: las reglas la dejan en
// `allow write: if false`. Un vehículo nuevo nace PENDIENTE y no se puede
// conducir hasta que un administrador vea la foto de la placa y la selfie —
// decisión del fundador: «debo ver qué está modificando el conductor».
export const callAgregarVehiculo = makeCallable<
  {
    tipo: 'CAR' | 'MOTORCYCLE' | 'MINIBUS' | 'BICYCLE';
    marca: string;
    modelo: string;
    color: string;
    anio?: string;
    placa: string;
    /** URL de Storage, en una carpeta de ESTE usuario. El servidor la congela. */
    fotoTrasera: string;
    selfie: string;
  },
  { ok: boolean; id: string; estado: 'PENDIENTE' }
>('agregarVehiculo');

export const callActivarVehiculo = makeCallable<
  { id: string },
  { ok: boolean; id: string; vehiculo: any }
>('activarVehiculo');

export const callBorrarVehiculo = makeCallable<{ id: string }, { ok: boolean; id: string }>(
  'borrarVehiculo'
);

// Panel de admin: la cola de vehículos por aprobar. Exigen claim admin + 2FA.
export const callListarVehiculosPendientes = makeCallable<
  { limit?: number },
  { ok: boolean; vehiculos: any[] }
>('listarVehiculosPendientes');

export const callRevisarVehiculo = makeCallable<
  { uid: string; id: string; decision: 'APROBADO' | 'RECHAZADO'; motivo?: string },
  { ok: boolean; decision: string }
>('revisarVehiculo');

// ── El seguro ───────────────────────────────────────────────────────────────
// El chofer manda la foto de su poliza nueva y nada mas: el servidor NO toca la
// fecha ni levanta el bloqueo (functions/src/seguro.ts). La fecha la escribe el
// administrador leyendo el documento — si la pusiera el chofer, se pondria la
// que le conviniera y el candado del seguro no serviria de nada.
export const callSubirSeguroNuevo = makeCallable<{ url: string }, { ok: boolean }>(
  'subirSeguroNuevo'
);

// ── El asistente de atencion al cliente ─────────────────────────────────────
// El usuario manda su pregunta y el SERVIDOR guarda la pregunta y la
// respuesta: el hilo es una constancia, asi que no lo escribe el telefono
// (ver `soporte_hilos` en firestore.rules). La pantalla lo lee en vivo.
// ── Publicidad de marcas ────────────────────────────────────────────────────
// `alcance` cuenta PERSONAS DISTINTAS POR DIA, no apariciones: es el numero
// con el que el fundador le pone precio al siguiente anunciante, asi que tiene
// que ser uno que aguante que se lo discutan. El servidor deduplica.
export const callContarAnuncio = makeCallable<
  { id: string; tipo: 'vista' | 'clic' },
  { ok: boolean; yaContado: boolean }
>('contarAnuncio');

export const callPreguntarAlAsistente = makeCallable<
  { texto: string },
  { ok: boolean; respuesta: string; escalado: boolean }
>('preguntarAlAsistente');

// ── Recarga de billetera por depósito/transferencia ─────────────────────────
// El usuario sube el comprobante a `recharge_receipts/{uid}/…` en Storage y
// manda la URL; el servidor valida que la foto sea SUYA y encola la solicitud.
// El saldo lo suma `resolveRechargeRequest` con Admin SDK tras la aprobación
// de un admin con 2FA — `walletBalance` está bloqueado al cliente en las
// reglas, así nadie se regala dinero.
// Sustituye al OCR de Railway, que está borrado (404) y dejaba la recarga
// fallando siempre.
export const callSubmitRechargeReceipt = makeCallable<
  { receiptUrl: string; bankId: string; declaredAmount?: number | null },
  { status: 'PENDING_REVIEW'; requestId: string; message: string }
>('submitRechargeReceipt');

export const callResolveRechargeRequest = makeCallable<
  { requestId: string; action: 'APPROVE' | 'REJECT'; amount?: number | null },
  { ok: boolean; status: 'APPROVED' | 'REJECTED'; credited: number; balanceAfter?: number }
>('resolveRechargeRequest');

export const callListPendingRecharges = makeCallable<
  void,
  { items: Array<{ id: string; userId: string | null; bankId: string | null; receiptUrl: string | null; declaredAmount: number | null; createdAt: number | null }> }
>('listPendingRecharges');

// ── Referidos ───────────────────────────────────────────────────────────────
// ensureReferralCode: devuelve (creando la 1ra vez) el código propio del usuario.
// applyReferralCode: canjea el código de quien te invitó → fija referredBy
// server-side (una sola vez). Activa el bono de chofer + viaje gratis de pasajero.
export const callEnsureReferralCode = makeCallable<void, { code: string }>(
  'ensureReferralCode'
);
export const callApplyReferralCode = makeCallable<
  { code: string },
  { ok: boolean; applied: boolean }
>('applyReferralCode');

// ── Stripe Connect onboarding del restaurante ───────────────────────────────
export const callRefreshStripeOnboarding = makeCallable<void, { url: string }>(
  'refreshStripeOnboarding'
);

export const callCheckRestaurantConnectStatus = makeCallable<
  void,
  { chargesEnabled: boolean; payoutsEnabled: boolean; detailsSubmitted: boolean }
>('checkRestaurantConnectStatus');

export interface ApproveRestaurantInput {
  restaurantId: string;
  decision: 'APPROVED' | 'REJECTED';
  reason?: string;
}

export const callApproveRestaurant = makeCallable<ApproveRestaurantInput, { ok: boolean }>(
  'approveRestaurant'
);

// ── Pausar / reactivar cuenta (admin) ───────────────────────────────────────
// Se usa cuando un comprobante de depósito NO coincide con lo que entró de
// verdad a la cuenta bancaria: se pausa al usuario hasta que valide el depósito.
export const callAdminSetAccountHold = makeCallable<
  { uid: string; hold: boolean; reason?: string },
  { ok: boolean; uid: string; hold: boolean }
>('adminSetAccountHold');

// ── Revisión del Papel de Buena Conducta (admin) ────────────────────────────
export const callReviewConductPaper = makeCallable<
  { driverUid: string; decision: 'APPROVED' | 'REJECTED'; reason?: string },
  { ok: boolean; driverUid: string; status: string }
>('reviewConductPaper');

// ── Rol y vehículo del chofer ───────────────────────────────────────────────
// `role` está bloqueado en firestore.rules (nadie se auto-asigna rol), así que
// esto pasa por el servidor. Sustituye al endpoint de Railway, que está caído:
// mientras dependió de él, elegir el medio de transporte NO guardaba nada y el
// chofer terminaba en el perfil equivocado.
export const callSetDriverRole = makeCallable<
  { role: 'DRIVER' | 'SCHOOL_DRIVER' | 'DELIVERY_DRIVER'; vehicleType?: string | null },
  { ok: boolean; role: string; vehicleType: string | null }
>('setDriverRole');

export const callSetAdminClaim = makeCallable<
  { uid: string; grant: boolean },
  { ok: boolean; admin: boolean }
>('setAdminClaim');

// ── Loyalty ──────────────────────────────────────────────────────────────────
export interface OnRideCompletedResult {
  ok: boolean;
  alreadyProcessed?: boolean;
  isFirstRideFree?: boolean;
  isLoyaltyFree?: boolean;
  newLoyalty?: {
    passengerTripsCompleted: number;
    freeTripsAvailable: number;
    isFirstRideFreeUsed: boolean;
    totalLifetimeTrips: number;
  };
}

export const callOnRideCompleted = makeCallable<{ rideId: string }, OnRideCompletedResult>(
  'onRideCompleted'
);

export const callMarkSchoolTrialStart = makeCallable<
  { schoolDriverId: string },
  { ok: boolean; alreadyStarted?: boolean; schoolStatus: 'TRIAL' | 'ACTIVE'; trialEndsAt?: number }
>('markSchoolTrialStart');

// ── Config dinámica ──────────────────────────────────────────────────────────
export interface StoreUrls {
  playStoreUrl: string;
  iosStoreUrl: string;
  webUrl: string;
  published: { android: boolean; ios: boolean };
}

export const callGetStoreUrls = makeCallable<void, StoreUrls>('getStoreUrls');

// ── Payouts seguros (valida ownership stripeAccountId server-side) ─────────
export const callRequestPayoutSecure = makeCallable<
  { amount: number; currency?: string },
  { success: boolean }
>('requestPayoutSecure');

// ── Auto-dispatch + take-order atómicos de food orders ─────────────────────
export const callClaimFoodOrder = makeCallable<
  { orderId: string },
  { ok: boolean; orderId: string }
>('claimFoodOrder');

export const callPickupFoodOrder = makeCallable<{ orderId: string }, { ok: boolean }>(
  'pickupFoodOrder'
);

// Entrega con PIN verificado SERVER-SIDE (anti-fraude de pago). El cliente
// NUNCA compara el PIN localmente ni escribe DELIVERED directo.
export const callDeliverFoodOrder = makeCallable<{ orderId: string; pin: string }, { ok: boolean }>(
  'deliverFoodOrder'
);

// ── Arranque de viaje con PIN verificado SERVER-SIDE ────────────────────────
// El chofer NUNCA ve el PIN (vive en /rides/{id}/private/security, legible solo
// por el pasajero) y las reglas prohíben poner status=IN_PROGRESS desde el
// cliente: esta es la única vía para arrancar un viaje.
export const callStartRideWithPin = makeCallable<
  { rideId: string; pin: string },
  { ok: boolean; rideId: string }
>('startRideWithPin');

// H63 — cierre de un ENVÍO de paquete. Dos caminos, los dos server-side:
//   · el código de 4 dígitos que dicta quien recibe el paquete,
//   · o la confirmación del remitente desde su propia pantalla.
export const callCompletarEnvioConPin = makeCallable<
  { rideId: string; pin: string },
  { ok: boolean; rideId: string }
>('completarEnvioConPin');

// EL CHOFER CONFIRMA QUE LE PAGARON EL VIAJE.
// El pago de un viaje va directo del pasajero al chofer —la plataforma no toca
// ese dinero—, así que hasta hoy no quedaba escrito en ningún sitio si el
// dinero llegó. Sin eso no hay forma de resolver un "yo sí te pagué" contra un
// "a mí no me llegó". Solo el chofer del viaje puede llamarla.
export const callConfirmarCobroDelViaje = makeCallable<
  { rideId: string; cobrado?: boolean },
  { ok: boolean; paymentStatus: string; sinCambios?: boolean }
>('confirmarCobroDelViaje');

export const callConfirmarEntregaDelEnvio = makeCallable<
  { rideId: string },
  { ok: boolean; rideId: string }
>('confirmarEntregaDelEnvio');

// ── Ratings ─────────────────────────────────────────────────────────────────
export interface SubmitOrderRatingInput {
  orderId: string;
  restaurantRating?: number;
  driverRating?: number;
  comment?: string;
}

export const callSubmitOrderRating = makeCallable<SubmitOrderRatingInput, { ok: boolean }>(
  'submitOrderRating'
);

export const callSubmitRideRating = makeCallable<
  { rideId: string; driverRating: number; comment?: string },
  { ok: boolean }
>('submitRideRating');

/**
 * El CHOFER califica al PASAJERO. La contraparte de la de arriba.
 * El servidor comprueba que quien llama sea el chofer de ESE viaje.
 */
export const callSubmitPassengerRating = makeCallable<
  { rideId: string; passengerRating: number; comment?: string },
  { ok: boolean }
>('submitPassengerRating');

// ── ETA aproximado de un pedido ────────────────────────────────────────────
export interface OrderEtaResult {
  etaMinutes: number;
  distanceKm: number;
  leg?: 'PICKUP' | 'DROPOFF' | 'PREPARING';
  status?: string;
}

export const callGetOrderEta = makeCallable<{ orderId: string }, OrderEtaResult>(
  'getOrderEta'
);

// ── Cupones / promo codes ──────────────────────────────────────────────────
export interface ValidateCouponInput {
  code: string;
  subtotal: number;
  kind?: 'FOOD' | 'RIDE';
}

export interface ValidateCouponResult {
  ok: boolean;
  discountAmount: number;
  finalTotal: number;
  message: string;
}

export const callValidateCoupon = makeCallable<ValidateCouponInput, ValidateCouponResult>(
  'validateCoupon'
);

// ── Places API (proxy seguro — key vive en Firebase Secrets) ────────────────
export interface PlaceSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
}

export const callGetPlaceSuggestions = makeCallable<
  { query: string; sessionToken?: string; userLat?: number; userLng?: number },
  { suggestions: PlaceSuggestion[] }
>('getPlaceSuggestions');

export interface PlaceDetails {
  placeId: string;
  formattedAddress: string;
  displayName: string;
  lat: number | null;
  lng: number | null;
}

export const callGetPlaceDetails = makeCallable<
  { placeId: string; sessionToken?: string },
  PlaceDetails
>('getPlaceDetails');

// ── Directions (ruta real por calles) + Geocoding ──────────────────────────
export interface RouteResult {
  coordinates: Array<{ lat: number; lng: number }>;
  distanceMeters: number;
  distanceText: string;
  durationSeconds: number;
  durationText: string;
  summary?: string;
}

export const callGetRoute = makeCallable<
  {
    origin: { lat: number; lng: number };
    destination: { lat: number; lng: number };
    mode?: 'driving' | 'walking' | 'bicycling';
  },
  RouteResult
>('getRoute');

export interface GeocodeResult {
  lat: number;
  lng: number;
  formattedAddress: string;
  /** Solo en la geocodificacion INVERSA: ¿la direccion nombra una calle? Si es
   *  false, lo unico especifico que hay es el Plus Code y hay que conservarlo
   *  (ver `direccionCorta`). Opcional: las respuestas cacheadas de antes de
   *  este cambio no lo traen, y `undefined` se trata como «no se sabe». */
  tieneCalle?: boolean;
}

export const callGeocodeAddress = makeCallable<{ address: string }, GeocodeResult | null>(
  'geocodeAddress'
);

export const callReverseGeocode = makeCallable<{ lat: number; lng: number }, GeocodeResult | null>(
  'reverseGeocode'
);

// ── Cancelación de pedidos con refund automático ──────────────────────────
export interface CancelOrderResult {
  ok: boolean;
  refundId?: string;
  refundAmountCents?: number;
  message: string;
}

export const callCancelFoodOrder = makeCallable<
  { orderId: string; reason?: string },
  CancelOrderResult
>('cancelFoodOrder');

// Onboarding del candidato Rent Car (enlace mágico) — el candidato logueado
// envía sus datos + documentos al CRM del dueño vía Admin SDK (las reglas no
// dejan que el candidato escriba directo en /users/{owner}/crm_drivers).
export const callSubmitCandidateOnboarding = makeCallable<
  { ownerId: string; name: string; phone: string; idDocUrl?: string | null; licenseUrl?: string | null; conductUrl?: string | null },
  { ok: boolean }
>('submitCandidateOnboarding');

// ── LA HUELLA DEL APARATO ───────────────────────────────────────────────────
//
// Ata esta cuenta al teléfono desde el que entró, para que un correo nuevo en
// el MISMO aparato no vuelva a cobrar lo que ya se regaló una vez. Es la cuarta
// llave de identidad; las otras tres (teléfono, correo, cédula) no sirven
// contra quien entra con Google: ver functions/src/dispositivo.ts.
//
// Se llama al entrar y se olvida: si falla, no se le dice nada a la persona
// (no es algo que ella pueda arreglar) y se reintenta en el siguiente arranque.
export const callRegistrarDispositivo = makeCallable<
  { huella: string },
  // El servidor devuelve TAMBIÉN el veredicto del tope de viajes sin
  // verificarse. No es un extra: la app lee el perfil UNA sola vez al arrancar
  // y puede habérselo leído antes de que esta llamada lo escribiera, así que
  // sin esto el aviso llegaría un arranque tarde — y ese arranque es
  // justamente el viaje gratis que se quiere evitar.
  {
    ok: boolean;
    nueva: boolean;
    requiereVerificacion?: boolean;
    viajesSinVerificar?: number;
    viajesSinVerificarTope?: number;
  }
>('registrarDispositivo');

/**
 * Pone al día lo que la persona arrastra de OTRAS cuentas suyas.
 *
 * No lleva parámetros a propósito: todo lo que decide sale de Auth y de
 * Firestore, nunca de lo que diga el teléfono. Por eso tampoco exige App
 * Check — y por eso puede correr en iOS y en web, donde `registrarDispositivo`
 * no existe. Ver la nota larga en functions/src/sincronizar.ts.
 */
export const callSincronizarMiCuenta = makeCallable<
  Record<string, never>,
  {
    ok: boolean;
    requiereVerificacion?: boolean;
    viajesSinVerificar?: number;
    viajesSinVerificarTope?: number;
    multaHeredada?: number;
    bloqueoHeredado?: boolean;
  }
>('sincronizarMiCuenta');

/**
 * Persiste el cambio de modo en el servidor.
 *
 * Nunca lanza por falta de permiso: devuelve `persistido: false` con el
 * motivo. La app hace su cambio local igual que siempre, asi que esto solo
 * puede mejorar lo que ya habia.
 */
export const callCambiarMiModo = makeCallable<
  { destino: string },
  { persistido: boolean; yaEstaba?: boolean; motivo?: string }
>('cambiarMiModo');

// ── APROBAR, RECHAZAR Y SOLTAR — el puente del rent car ─────────────────────
//
// Lo que había (`approveCandidateDriver` en useAppStore) vivía ENTERO en el
// teléfono del dueño y de lo que prometía no hacía casi nada: tiraba el
// vehículo elegido, no marcaba el carro como entregado —`'Rentado'` tenía
// cuatro lectores y CERO escritores en todo el proyecto— y no avisaba al
// candidato. Y «rechazar» solo lo quitaba de la lista en memoria: volvía a
// aparecer al recargar.
//
// Esto toca DOS documentos de DOS dueños distintos a la vez (el vehículo del
// dueño y el perfil del candidato), así que no se podía hacer desde el cliente
// ni arreglándolo. Ver functions/src/drivers.ts.
export const callAprobarCandidato = makeCallable<
  { candidateUid: string; vehicleId: string },
  { ok: boolean; vehiculo: string }
>('aprobarCandidatoDeFlota');

export const callRechazarCandidato = makeCallable<
  { candidateUid: string; motivo?: string },
  { ok: boolean }
>('rechazarCandidatoDeFlota');

/** El chofer devolvió el carro: vuelve a estar libre y publicable. */
export const callLiberarVehiculo = makeCallable<
  { vehicleId: string },
  { ok: boolean }
>('liberarVehiculoDeFlota');

// Cuota del chofer con TARJETA (functions/src/pagoCuotaStripe.ts). No se manda
// ningún monto: el servidor calcula cuánto debe y devuelve la página de pago
// de Stripe. El saldo lo acredita el servidor cuando Stripe confirma el cobro.
export const callCrearPagoCuota = makeCallable<
  Record<string, never>,
  { url: string; monto: number; sesionId: string }
>('crearPagoCuota');

// Al volver de la página de pago: el servidor mira el cobro en Stripe y, si
// está pagado, lo acredita en el acto (no espera al webhook). Sin `sesionId`
// usa el cobro abierto del chofer (arranque en frío al volver).
/**
 * ¿Está bloqueada esta conversación, en cualquiera de los dos sentidos?
 *
 * Lo pregunta el que PUEDE haber sido bloqueado: su propio documento de bloqueo
 * no lo puede leer (es privado del otro, y tiene que serlo). El servidor mira
 * los dos lados y responde solo sí o no — nunca quién bloqueó a quién.
 */
/**
 * Resuelve el destino de un viaje que llegó sin coordenadas y lo guarda.
 * Lo pide el lanzador de navegación justo antes de abrir Waze, que sin un
 * punto abre el mapa en blanco.
 */
export const callResolverDestinoDelViaje = makeCallable<
  { rideId: string },
  { lat: number; lng: number; aproximado?: boolean; yaEstaba?: boolean }
>('resolverDestinoDelViaje');

/**
 * Abre un cobro con tarjeta para RECARGAR la billetera (mínimo RD$50).
 * El monto lo valida el servidor; aquí solo se propone.
 */
export const callCrearRecargaTarjeta = makeCallable<
  { monto: number },
  { url: string; monto: number; sesionId: string }
>('crearRecargaTarjeta');

export const callChatBloqueado = makeCallable<
  { otroUid: string },
  { bloqueado: boolean }
>('chatBloqueado');

export const callConfirmarPagoCuota = makeCallable<
  { sesionId?: string },
  { estado: 'ACREDITADO' | 'PENDIENTE' | 'VENCIDO' | 'REVISAR' | 'SIN_COBRO'; monto?: number }
>('confirmarPagoCuota');

// ════════════════════════════════════════════════════════════════════════
// LOGÍSTICA — curier, mudanza, carga y autobús para gira
// ════════════════════════════════════════════════════════════════════════
//
// El renglon de vehículos de mayor categoría. Dos cosas que importan al leer
// esto:
//
//   · NO cambia el rol de nadie. El dueño de la camioneta sigue siendo
//     pasajero; se le añade una capacidad, no se le cambia de gremio.
//   · NO paga mensualidad. Solo comisión por servicio hecho, y la tabla la
//     calcula `constants/comisionLogistica.ts` — que es espejo exacto de la del
//     servidor, vigilado por `scripts/probar-espejos.mjs`.
//
// El servidor es `functions/src/logistica.ts`. El nombre del string tiene que
// coincidir EXACTAMENTE con el export de `functions/src/index.ts`: una callable
// que no se exporta ahí no se despliega, y falla en silencio.

export type TipoVehiculoLogistica = 'CAMIONETA' | 'CAMION' | 'VAN' | 'AUTOBUS';
export type ServicioLogistica = 'CURIER' | 'MUDANZA' | 'CARGA' | 'GIRA';
export type EstadoProveedorLogistica = 'PENDIENTE' | 'APROBADO' | 'RECHAZADO';

/**
 * Da de alta (o corrige) el vehículo de carga de quien llama. Nace siempre
 * PENDIENTE: lo aprueba una persona de Go Safely después de mirar los cuatro
 * papeles. El motivo lo dio el fundador hablando del otro renglón —
 * «cualquier usuario puede engañar a nuestro mensajero con foto falsa si no
 * cuenta con una verificación»— y aquí lo que está en juego es más: alguien
 * le entrega los muebles de su casa a un desconocido.
 */
export const callRegistrarProveedorLogistica = makeCallable<
  {
    tipoVehiculo: TipoVehiculoLogistica;
    servicios: ServicioLogistica[];
    nombre: string;
    telefono: string;
    placa: string;
    capacidad: string;
    zonas: string;
    fotoVehiculoUrl: string;
    fotoMatriculaUrl: string;
    fotoLicenciaUrl: string;
    fotoSeguroUrl: string;
  },
  { ok: true; estado: EstadoProveedorLogistica }
>('registrarProveedorLogistica');

/** Un trabajo nuevo. Lo CREA el servidor: el teléfono solo manda argumentos. */
export const callCrearServicioLogistica = makeCallable<
  {
    tipo: ServicioLogistica;
    descripcion: string;
    origen: { texto: string; lat: number | null; lng: number | null };
    destino: { texto: string; lat: number | null; lng: number | null };
    fechaDeseadaMs: number;
    montoOfrecido: number | null;
  },
  { servicioId: string; estado: string }
>('crearServicioLogistica');

/**
 * El escaparate del transportista. Es una CALLABLE y no una consulta, y la
 * diferencia no es de estilo: en Firestore una consulta se evalúa contra la
 * REGLA, no contra los documentos que devuelve, así que dejar listar los
 * trabajos abiertos sería servir la dirección de casa de cada cliente en vivo.
 * Esto proyecta: el sector y una coordenada redondeada a un kilómetro, nunca
 * la calle.
 */
export const callEscaparateDeLogistica = makeCallable<
  Record<string, never>,
  {
    trabajos: Array<{
      servicioId: string;
      tipo: string;
      descripcion: string;
      fechaDeseadaMs: number | null;
      montoOfrecido: number | null;
      sectorOrigen: string | null;
      sectorDestino: string | null;
      origenAprox: { lat: number; lng: number } | null;
      destinoAprox: { lat: number; lng: number } | null;
      distanciaKm: number | null;
      cuantasCotizaciones: number;
      yaCotice: boolean;
      creadoMs: number | null;
    }>;
    puedeCotizar: boolean;
    hayMas: boolean;
    deudaPendiente?: number;
    motivo?: string;
  }
>('escaparateDeLogistica');

/** Poner precio. Devuelve el desglose para enseñárselo ANTES de confirmar. */
export const callCotizarServicioLogistica = makeCallable<
  { servicioId: string; monto: number },
  { monto: number; comision: number; recibe: number; porcientoEfectivo: number }
>('cotizarServicioLogistica');

/** El cliente elige. Aquí nace el PIN de entrega, en la misma transacción. */
export const callAceptarCotizacionLogistica = makeCallable<
  { servicioId: string; proveedorUid: string },
  { ok: boolean; yaEstaba?: boolean; montoAcordado: number; comision: number; recibe: number }
>('aceptarCotizacionLogistica');

export const callIniciarServicioLogistica = makeCallable<
  { servicioId: string },
  { ok: boolean; estado: string; yaEstaba?: boolean }
>('iniciarServicioLogistica');

/** El PIN lo dicta el CLIENTE al recibir. Sin él no hay entrega ni comisión. */
export const callCompletarServicioLogistica = makeCallable<
  { servicioId: string; pin: string },
  { ok: boolean; estado: string; comision: number; recibe: number; comisionPendiente: number }
>('completarServicioLogistica');

export const callCancelarServicioLogistica = makeCallable<
  { servicioId: string; motivo: string },
  { ok: boolean; estado: string; yaEstaba?: boolean }
>('cancelarServicioLogistica');

/**
 * El teléfono del otro, y solo con trato cerrado. Las reglas cierran el alta
 * del proveedor (lleva su placa y las fotos de su licencia y su seguro) y
 * /users también, así que sin esto nadie podía llamar a nadie para coordinar
 * una entrega.
 */
export const callContactoDelServicioLogistica = makeCallable<
  { servicioId: string },
  {
    quien: 'PROVEEDOR' | 'CLIENTE';
    nombre: string;
    telefono: string;
    tipoVehiculo?: string;
    placa?: string;
  }
>('contactoDelServicioLogistica');

export const callMiDeudaDeLogistica = makeCallable<
  Record<string, never>,
  {
    debe: number;
    bloqueadoPorDeuda: boolean;
    estado: string;
    serviciosHechos: number;
    servicios: Array<{ servicioId: string; monto: number; comision: number; creadoMs: number }>;
    cobroAgrupadoDesde: number;
    topeDeSaldo: number;
  }
>('miDeudaDeLogistica');

/**
 * ══ EL MEDIO DE PAGO ════════════════════════════════════════════════
 *
 * **La tarjeta NO se guarda.** Decisión del fundador: «que no se guarde y
 * que sea opcional; en el momento de terminar un servicio entran nuestros
 * métodos: recargar la app, transferencia, o el tope de saldo».
 *
 * Por eso aquí solo hay UN envoltorio de pago. Hubo tres más — guardar,
 * confirmar y quitar una tarjeta— y se quitaron con su servidor: los
 * Términos y la Política de Privacidad dicen que no guardamos tarjetas, y
 * eso sigue siendo verdad.
 *
 * Lo que queda: la comisión se cobra de la BILLETERA en el momento de
 * entregar, y si no alcanza, con este enlace de Checkout de un solo uso.
 */
export const callPagarComisionAhora = makeCallable<
  Record<string, never>,
  { url: string; monto: number; sesionId: string }
>('pagarComisionAhora');

// ⚠ `adminRevisarProveedorLogistica` NO tiene envoltorio aquí, y es a
// propósito: la consola de admin vive FUERA de la app (`public/admin/`, ver la
// nota de seguridad de `(tabs)/profile.tsx`) y llama a la callable directamente
// por su nombre con `httpsCallable`. Un envoltorio aquí sería código que nadie
// importa — y lo peor, invitaría a meter un acceso de admin dentro de la app.
